import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { motion } from 'motion/react'
import { ClipboardList, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ActivityFilter, ActivityItem } from './types'
import { fetchActivityFeed } from './api'
import { useExpandable } from '../../shared/hooks/useExpandable'
import { BackButton, SectionHeading } from '@tendbot/ui'
import { SkeletonListItem } from '@tendbot/ui'
import { defaultStagger } from '@tendbot/ui'
import { ActivityCard, ActivityFilterBar, ActivityEmptyState } from './components'

function getDateGroup(iso: string, t: (key: string) => string): string {
  const date = new Date(iso)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date >= today) return t('dateSeparator.today')
  if (date >= yesterday) return t('dateSeparator.yesterday')
  return date.toLocaleDateString('en-US', { weekday: 'long' })
}

const FILTER_EVENT_MAP: Record<ActivityFilter, string[]> = {
  all: [],
  emails: ['email_sent'],
  approvals: ['approval_resolved'],
  tools: ['tool_call'],
  notebook: ['notebook_change'],
  nudges: ['nudge_delivered', 'briefing_delivered'],
}

interface Props {
  onBack?: () => void
}

export default function ActivityScreen({ onBack }: Props) {
  const { t } = useTranslation('activity')
  const [activeFilter, setActiveFilter] = useState<ActivityFilter>('all')
  const expandState = useExpandable()

  const [items, setItems] = useState<ActivityItem[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Track current filter to avoid stale responses
  const filterRef = useRef(activeFilter)
  filterRef.current = activeFilter

  // Fetch items when filter changes
  useEffect(() => {
    const currentFilter = activeFilter
    setLoading(true)
    setError(null)
    setItems([])
    setNextCursor(null)

    const eventType = FILTER_EVENT_MAP[currentFilter]
    fetchActivityFeed({ eventType: eventType.length > 0 ? eventType : undefined })
      .then(res => {
        if (filterRef.current !== currentFilter) return
        setItems(res.items)
        setNextCursor(res.nextCursor)
      })
      .catch(err => {
        if (filterRef.current !== currentFilter) return
        setError(err instanceof Error ? err.message : 'Failed to load activity')
      })
      .finally(() => {
        if (filterRef.current !== currentFilter) return
        setLoading(false)
      })
  }, [activeFilter])

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return
    setLoadingMore(true)
    try {
      const eventType = FILTER_EVENT_MAP[activeFilter]
      const res = await fetchActivityFeed({
        cursor: nextCursor,
        eventType: eventType.length > 0 ? eventType : undefined,
      })
      setItems(prev => [...prev, ...res.items])
      setNextCursor(res.nextCursor)
    } catch {
      // Silently fail load-more — user can retry
    } finally {
      setLoadingMore(false)
    }
  }, [nextCursor, loadingMore, activeFilter])

  const grouped = useMemo(() => {
    const groups: { label: string; items: ActivityItem[] }[] = []
    let currentLabel = ''
    for (const item of items) {
      const label = getDateGroup(item.createdAt, t)
      if (label !== currentLabel) {
        currentLabel = label
        groups.push({ label, items: [item] })
      } else {
        groups[groups.length - 1]!.items.push(item)
      }
    }
    return groups
  }, [items, t])

  const handleFilter = useCallback((f: ActivityFilter) => {
    setActiveFilter(f)
    expandState.collapse()
  }, [expandState])

  const { container: containerVariants, item: itemVariants } = defaultStagger

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="h-full overflow-y-auto px-5 sm:px-8 lg:px-12 py-4"
    >
      <div className="max-w-2xl mx-auto">
        {/* Back button (only when accessed as sub-screen) */}
        {onBack && <BackButton onClick={onBack} />}

        {/* Header */}
        <motion.div variants={itemVariants} className="mb-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
              <ClipboardList size={20} className="text-gold" />
            </div>
            <div>
              <h2 className="font-display text-lg text-ivory font-semibold">{t('title')}</h2>
              {!loading && items.length > 0 && (
                <p className="text-pearl/60 text-xs">
                  {t('eventsCount', { count: items.length })}
                </p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Filter chips */}
        <motion.div variants={itemVariants} className="mb-5 border-b border-gold/10">
          <ActivityFilterBar active={activeFilter} onFilter={handleFilter} />
        </motion.div>

        {/* Loading state */}
        {loading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonListItem key={i} />
            ))}
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
            <p className="text-rose-400 text-sm">{error}</p>
          </motion.div>
        )}

        {/* Empty state */}
        {!loading && !error && items.length === 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <ActivityEmptyState filtered={activeFilter !== 'all'} />
          </motion.div>
        )}

        {/* Date-grouped feed */}
        {!loading && grouped.map(group => (
          <motion.div
            key={group.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-5"
          >
            <SectionHeading>{group.label}</SectionHeading>
            <div className="space-y-2">
              {group.items.map(item => (
                <ActivityCard
                  key={item.id}
                  item={item}
                  isExpanded={expandState.isExpanded(item.id)}
                  onToggle={() => expandState.toggle(item.id)}
                />
              ))}
            </div>
          </motion.div>
        ))}

        {/* Load more */}
        {nextCursor && !loading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-center py-4">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gold/80 hover:text-gold
                border border-gold/20 hover:border-gold/40 rounded-lg transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore && <Loader2 size={14} className="animate-spin" />}
              {loadingMore ? t('loadingMore') : t('loadMore')}
            </button>
          </motion.div>
        )}

        {/* Bottom spacer */}
        <div className="h-8" />
      </div>
    </motion.div>
  )
}
